import React, { useState } from 'react';
import {
    User,
    Edit3,
    Save,
    X,
    Mail,
    Phone,
    MapPin,
    CreditCard,
    Trophy,
    Users,
    FileText,
    ArrowUp,
    Briefcase,
    Calendar,
    Shield,
    Camera,
    Star
} from 'lucide-react';

import MemberCard from './MemberCard';
import MemberBadges from './MemberBadges';
import MemberReferrals from './MemberReferrals';
import MemberDocuments from './MemberDocuments';
import MemberUpgrade from './MemberUpgrade';
import { MembershipLevel } from '../../types/adhesion.types';

interface MemberProfileCompleteProps {
    memberData: any;
    permissions: any;
    membershipLevel: MembershipLevel;
    qrCodeUrl?: string;
    showQRModal?: boolean;
    isDownloadingCard?: boolean;
    isDownloadingPDF?: boolean;
    downloadError?: string | null;
    pdfError?: string | null;
    copySuccess?: boolean;
    onQRClick?: () => void;
    onCloseQRModal?: () => void;
    onDownloadCard?: () => void;
    onGeneratePDF?: () => void;
    onClearDownloadError?: () => void;
    onClearPdfError?: () => void;
    onDownloadQR?: () => void;
    onCopyLink?: () => void;
    onShareWhatsApp?: () => void;
    onShareSocial?: (platform: 'telegram' | 'twitter' | 'facebook' | 'linkedin') => void;
    onUpdate?: (updatedData: any) => void;
    shareLinks?: any;
}

const MemberProfileComplete: React.FC<MemberProfileCompleteProps> = (props) => {
    const [activeTab, setActiveTab] = useState('infos');
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        first_name: props.memberData.first_name || '',
        last_name: props.memberData.last_name || '',
        email: props.memberData.email || '',
        phone: props.memberData.phone || '',
        location: props.memberData.location || '',
        profession: props.memberData.profession || '',
        bio: props.memberData.bio || ''
    });

    const getMemberLevelIcon = () => {
        switch (props.membershipLevel) {
            case 'full_member':
                return <Shield className="w-4 h-4 text-green-600" />;
            case 'partial_member':
                return <Star className="w-4 h-4 text-yellow-600" />;
            default:
                return <User className="w-4 h-4 text-blue-600" />;
        }
    };

    const getMemberLevelLabel = () => {
        switch (props.membershipLevel) {
            case 'full_member': return 'Membre Complet';
            case 'partial_member': return 'Membre Partiel';
            case 'sympathisant': return 'Sympathisant';
            default: return 'Membre';
        }
    };

    const handleSave = () => {
        if (props.onUpdate) {
            props.onUpdate({ ...props.memberData, ...editForm });
        }
        setIsEditing(false);
    };

    const tabs = [
        { id: 'infos', label: 'Informations personnelles', icon: <User className="w-4 h-4" /> },
        { id: 'card', label: 'Ma carte membre', icon: <CreditCard className="w-4 h-4" />, permission: 'canReceiveMemberCard' },
        { id: 'badges', label: 'Mes badges', icon: <Trophy className="w-4 h-4" /> },
        { id: 'referrals', label: 'Parrainages', icon: <Users className="w-4 h-4" />, permission: 'canReferMembers' },
        { id: 'documents', label: 'Documents', icon: <FileText className="w-4 h-4" />, permission: 'canDownloadOfficialDocuments' },
        { id: 'upgrade', label: 'Améliorer mon compte', icon: <ArrowUp className="w-4 h-4" />, hideFor: 'full_member' }
    ].filter(tab => {
        if (tab.permission && !props.permissions[tab.permission]) return false;
        if (tab.hideFor && props.membershipLevel === tab.hideFor) return false;
        return true;
    });

    return (
        <div className="space-y-6">
            {/* Header avec photo de profil */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center space-x-6">
                    <div className="relative">
                        {props.memberData.selfie_url || props.memberData.avatar_url || props.memberData.photo_url ? (
                            <img 
                                src={props.memberData.selfie_url || props.memberData.avatar_url || props.memberData.photo_url}
                                alt="Profil membre"
                                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                                onError={(e) => {
                                    // En cas d'erreur de chargement, afficher les initiales
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                        const fallback = parent.querySelector('.fallback-initials');
                                        if (fallback) {
                                            (fallback as HTMLElement).style.display = 'flex';
                                        }
                                    }
                                }}
                            />
                        ) : null}
                        <div className={`w-24 h-24 bg-gradient-to-br from-blue-600 to-green-600 rounded-full flex items-center justify-center text-white text-2xl font-bold fallback-initials ${
                            props.memberData.selfie_url || props.memberData.avatar_url || props.memberData.photo_url ? 'hidden' : 'flex'
                        }`}>
                            {props.memberData.first_name?.charAt(0)}{props.memberData.last_name?.charAt(0)}
                        </div>
                        <button className="absolute -bottom-2 -right-2 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors">
                            <Camera className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                            {props.memberData.first_name} {props.memberData.last_name}
                        </h1>
                        <div className="flex items-center space-x-2 mb-2">
                            {getMemberLevelIcon()}
                            <span className="text-gray-600">{getMemberLevelLabel()}</span>
                        </div>
                        <p className="text-gray-600">{props.memberData.email}</p>
                        {props.memberData.nip && (
                            <p className="text-sm text-gray-500">NIP: {props.memberData.nip}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Navigation par onglets */}
            <div className="bg-white rounded-lg shadow-sm">
                <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8 px-6">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors ${
                                    activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                {tab.icon}
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Contenu des onglets */}
                <div className="p-6">
                    {activeTab === 'infos' && (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-lg font-semibold text-gray-900">Informations personnelles</h3>
                                <button
                                    onClick={() => setIsEditing(!isEditing)}
                                    className="flex items-center space-x-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                    {isEditing ? <X className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                                    <span>{isEditing ? 'Annuler' : 'Modifier'}</span>
                                </button>
                            </div>

                            {isEditing ? (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                                            <input
                                                type="text"
                                                value={editForm.first_name}
                                                onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                                            <input
                                                type="text"
                                                value={editForm.last_name}
                                                onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                            <input
                                                type="email"
                                                value={editForm.email}
                                                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                                            <input
                                                type="tel"
                                                value={editForm.phone}
                                                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Lieu</label>
                                            <input
                                                type="text"
                                                value={editForm.location}
                                                onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Profession</label>
                                            <input
                                                type="text"
                                                value={editForm.profession}
                                                onChange={(e) => setEditForm({...editForm, profession: e.target.value})}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
                                        <textarea
                                            rows={4}
                                            value={editForm.bio}
                                            onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Parlez-nous de vous..."
                                        />
                                    </div>
                                    <div className="flex justify-end space-x-4">
                                        <button
                                            onClick={() => setIsEditing(false)}
                                            className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                                        >
                                            Annuler
                                        </button>
                                        <button
                                            onClick={handleSave}
                                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            <Save className="w-4 h-4" />
                                            <span>Enregistrer</span>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="flex items-center space-x-3">
                                            <Mail className="w-5 h-5 text-gray-400" />
                                            <div>
                                                <div className="text-sm text-gray-500">Email</div>
                                                <div className="font-medium">{props.memberData.email}</div>
                                            </div>
                                        </div>
                                        {props.memberData.phone && (
                                            <div className="flex items-center space-x-3">
                                                <Phone className="w-5 h-5 text-gray-400" />
                                                <div>
                                                    <div className="text-sm text-gray-500">Téléphone</div>
                                                    <div className="font-medium">{props.memberData.phone}</div>
                                                </div>
                                            </div>
                                        )}
                                        {props.memberData.location && (
                                            <div className="flex items-center space-x-3">
                                                <MapPin className="w-5 h-5 text-gray-400" />
                                                <div>
                                                    <div className="text-sm text-gray-500">Lieu</div>
                                                    <div className="font-medium">{props.memberData.location}</div>
                                                </div>
                                            </div>
                                        )}
                                        {props.memberData.profession && (
                                            <div className="flex items-center space-x-3">
                                                <Briefcase className="w-5 h-5 text-gray-400" />
                                                <div>
                                                    <div className="text-sm text-gray-500">Profession</div>
                                                    <div className="font-medium">{props.memberData.profession}</div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex items-center space-x-3">
                                            <Calendar className="w-5 h-5 text-gray-400" />
                                            <div>
                                                <div className="text-sm text-gray-500">Membre depuis</div>
                                                <div className="font-medium">
                                                    {new Date(props.memberData.created_at || Date.now()).toLocaleDateString('fr-FR')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {props.memberData.bio && (
                                        <div>
                                            <div className="text-sm text-gray-500 mb-2">Bio</div>
                                            <p className="text-gray-700">{props.memberData.bio}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'card' && (
                        <MemberCard
                            memberData={props.memberData}
                            qrCodeUrl={props.qrCodeUrl}
                            showQRModal={props.showQRModal || false}
                            isDownloadingCard={props.isDownloadingCard || false}
                            isDownloadingPDF={props.isDownloadingPDF || false}
                            downloadError={props.downloadError}
                            pdfError={props.pdfError}
                            onQRClick={props.onQRClick || (() => {})}
                            onCloseQRModal={props.onCloseQRModal || (() => {})}
                            onDownloadCard={props.onDownloadCard || (() => {})}
                            onGeneratePDF={props.onGeneratePDF || (() => {})}
                            onClearDownloadError={props.onClearDownloadError || (() => {})}
                            onClearPdfError={props.onClearPdfError || (() => {})}
                            onDownloadQR={props.onDownloadQR || (() => {})}
                        />
                    )}

                    {activeTab === 'badges' && (
                        <MemberBadges memberData={props.memberData} />
                    )}

                    {activeTab === 'referrals' && (
                        <MemberReferrals
                            memberData={props.memberData}
                            shareLinks={props.shareLinks}
                            copySuccess={props.copySuccess || false}
                            onCopyLink={props.onCopyLink || (() => {})}
                            onShareWhatsApp={props.onShareWhatsApp || (() => {})}
                            onShareSocial={props.onShareSocial || (() => {})}
                        />
                    )}

                    {activeTab === 'documents' && (
                        <MemberDocuments
                            memberData={props.memberData}
                            permissions={props.permissions}
                        />
                    )}

                    {activeTab === 'upgrade' && (
                        <MemberUpgrade
                            memberData={props.memberData}
                            currentLevel={props.membershipLevel}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default MemberProfileComplete;